import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '@sabre/spark-react-core';
import { array, bool, string } from 'prop-types';
import { useCorePricingContext } from '../../contexts/core-pricing-context.jsx';
import AgGridReactTable from '../../ui-components/ag-gird-table/ag-grid-react-table.jsx';
import DistributionDeleteConfirmation from '../../ui-components/core-pricing-component/distribution-delete-confirmation-modal/distribution-delete-confirmation-modal.jsx';
import Loader from '../../ui-components/loader/loader.jsx';
import { SIZE } from '../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../utils/notification-messages.js';
import { toastSuccess, toastWarn } from '../../utils/toasts.js';
import { getContextMenuConfig, saveChanges } from './__helpers__/functions';
import { MARKET_LIST_TABLE_HEADERS } from './__helpers__/tableHeaders';

function MarketListTable({
   rowData: rowDataFromParent = [],
   isLoading = false,
}) {
   const [rowData, setRowData] = useState([]);
   const [agGridApi, setAgGridApi] = useState(null);
   const customContextMenuConfig = getContextMenuConfig(rowData);
   const [selectedRows, setSelectedRows] = useState(['0']);
   const [showDeleteConformationPopup, setShowDeleteConformationPopup] =
      useState(false);
   const [deleteMarketListData, setDeleteMarketListData] = useState({});
   const { activeScreen } = useCorePricingContext();
   const [recordCount, setRecordCount] = useState(0);

   const rowSelectionIndicator =
      selectedRows.length > 1
         ? `${selectedRows.length} records selected`
         : `Record ${selectedRows[0]} selected`;

   const onSaveChanges = async (editedData, rowData) => {
      const isInvalidPayload = editedData.some((item) => {
         return (
            !('marketId' in item) ||
            !item.marketId?.trim() ||
            !('origin' in item) ||
            !item.origin?.trim() ||
            !('destination' in item) ||
            !item.destination?.trim()
         );
      });

      if (isInvalidPayload) {
         toastWarn(
            NOTIFICATION_MESSAGES.MARKET_LIST_MISSING_REQUIRED_FIELDS_MESSAGE
         );
         return;
      }
      const savedMarketListData = await saveChanges(editedData, rowData);
      if (savedMarketListData.length > 1) setRowData(savedMarketListData);
      setRowData(savedMarketListData);
   };

   const handleDeletePopupClose = () => {
      setShowDeleteConformationPopup(false);
   };

   const handleDeleteApiCallBack = (rowsToDelete) => {
      setShowDeleteConformationPopup(false);
      toastSuccess(NOTIFICATION_MESSAGES.MARKET_LIST_DELETE_SUCCESS);
      agGridApi?.applyTransaction({
         remove: rowsToDelete,
      });
   };
   function onDeleteChanges(payload, gridApi) {
      setAgGridApi(gridApi);
      setDeleteMarketListData(payload);
      setShowDeleteConformationPopup(true);
   }

   const updateSelectedRows = useCallback((selectedNodes) => {
      const selectedRowIndices = selectedNodes?.map(
         (node) => node.rowIndex + 1
      );
      setSelectedRows(selectedRowIndices.length ? selectedRowIndices : ['0']);
   }, []);

   const onModelUpdated = (params) => {
      const displayedRows = params.api.getDisplayedRowCount();
      const selectedRowIndices = params.api
         .getSelectedNodes()
         .map((node) => node.rowIndex + 1);
      setSelectedRows(selectedRowIndices);
      setRecordCount(displayedRows);
   };

   useEffect(() => {
      setRowData(rowDataFromParent);
   }, [rowDataFromParent]);

   return (
      <section className='core-pricing-market-list'>
         {showDeleteConformationPopup && (
            <div className='marketlist-delete-popup'>
               <Modal
                  onClose={handleDeletePopupClose}
                  open={showDeleteConformationPopup}
                  titleHeadingLevel={3}
                  modalId='marketlist-popup-modal'
                  className={`marketlist-popup-modal${
                     showDeleteConformationPopup ? '' : ' spark-hidden'
                  } `}
                  contentClassName='col-xs-12 col-sm-12 col-md-11 col-lg-8 col-xl-6'
               >
                  <DistributionDeleteConfirmation
                     distributionListToDelete={deleteMarketListData}
                     onUpdateDistributionTable={handleDeleteApiCallBack}
                     onCancelButtonClick={handleDeletePopupClose}
                     selectedScreen={activeScreen}
                  />
               </Modal>
            </div>
         )}
         <div className='spark-flex core-pricing-market-list-table'>
            {isLoading ? (
               <div className='loader'>
                  <Loader size={SIZE.SMALL} />
               </div>
            ) : (
               <>
                  <AgGridReactTable
                     rowData={rowData}
                     headers={MARKET_LIST_TABLE_HEADERS}
                     enableMultiRowSelection
                     enableContextMenu
                     rememberSelectedRow
                     enableRowIds
                     onSelectedRowsChange={updateSelectedRows}
                     contextMenuConfig={{
                        ...customContextMenuConfig,
                        onDeleteChanges,
                        onSaveChanges,
                     }}
                     onModelUpdated={onModelUpdated}
                  />
                  <p className='spark-bold spark-flex justify-between spark-pad-t-.5 row-selection-info'>
                     {rowSelectionIndicator} (Total {recordCount})
                  </p>
               </>
            )}
         </div>
      </section>
   );
}

MarketListTable.propTypes = {
   rowData: array,
   searchStatus: string,
   isLoading: bool,
   selectedScreen: string,
};

export default MarketListTable;
